import { z } from "zod";
import { buildRecordFromKeyList } from "../build-record-from-key-list";
import { computeFloorAreaSquareMetre } from "./building-stock";
import { FULL_RETROFIT_COMPONENT_KEY, isEnvelopeComponentKey } from "./envelope-component";
import { isHeatingTypeKey } from "./heating-type";
import { listProfileBuildingTypeKeys } from "./list-profile-building-type-keys";
import { isRetrofitTargetStandardKey } from "./retrofit-target-standard";
const MIN_LIST_LENGTH = 1;
const nonNegativeCount = z.number().int().min(0);
export const ENVELOPE_RETROFIT_ISSUE_KEY = {
    DUPLICATE_HEATING_TYPE: "duplicateHeatingType",
    DUPLICATE_ENVELOPE_COMPONENT: "duplicateEnvelopeComponent",
    FULL_RETROFIT_EXCLUSIVE: "fullRetrofitExclusive",
    TOTAL_FLOOR_AREA_ZERO: "totalFloorAreaZero",
};
const AffectedConsumptionEntrySchema = z.object({
    heatingType: z.custom(isHeatingTypeKey),
    consumptionMwh: z.number().positive(),
});
const BuildingStockEntrySchema = z.object({
    count: nonNegativeCount,
    floorAreaSquareMetre: z.number().min(0),
    isDefaultFloorArea: z.boolean(),
});
const hasDuplicate = (valueList) => new Set(valueList).size !== valueList.length;
const buildBuildingStockShape = (profile) => Object.fromEntries(listProfileBuildingTypeKeys(profile).map((buildingType) => [buildingType, BuildingStockEntrySchema]));
const pickBuildingStock = (stock, profile) => buildRecordFromKeyList(listProfileBuildingTypeKeys(profile), (buildingType) => stock[buildingType]);
export function buildEnvelopeRetrofitParametersSchema(profile) {
    return z
        .object({
        affectedConsumptionList: z.array(AffectedConsumptionEntrySchema).min(MIN_LIST_LENGTH),
        buildingStock: z.object(buildBuildingStockShape(profile)),
        envelopeComponentList: z.array(z.custom(isEnvelopeComponentKey)).min(MIN_LIST_LENGTH),
        targetStandard: z.custom(isRetrofitTargetStandardKey),
    })
        .superRefine((parameters, ctx) => {
        if (hasDuplicate(parameters.affectedConsumptionList.map((entry) => entry.heatingType))) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["affectedConsumptionList"],
                params: { messageKey: ENVELOPE_RETROFIT_ISSUE_KEY.DUPLICATE_HEATING_TYPE },
            });
        }
        if (hasDuplicate(parameters.envelopeComponentList)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["envelopeComponentList"],
                params: { messageKey: ENVELOPE_RETROFIT_ISSUE_KEY.DUPLICATE_ENVELOPE_COMPONENT },
            });
        }
        const isFullRetrofit = parameters.envelopeComponentList.includes(FULL_RETROFIT_COMPONENT_KEY);
        if (isFullRetrofit && parameters.envelopeComponentList.length > MIN_LIST_LENGTH) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["envelopeComponentList"],
                params: { messageKey: ENVELOPE_RETROFIT_ISSUE_KEY.FULL_RETROFIT_EXCLUSIVE },
            });
        }
        const buildingStock = pickBuildingStock(parameters.buildingStock, profile);
        if (computeFloorAreaSquareMetre({ buildingStock, profile }) <= 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["buildingStock"],
                params: { messageKey: ENVELOPE_RETROFIT_ISSUE_KEY.TOTAL_FLOOR_AREA_ZERO },
            });
        }
    })
        .transform((parameters) => ({
        ...parameters,
        buildingStock: pickBuildingStock(parameters.buildingStock, profile),
    }));
}
